import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCreatePackageMutation, useUpdatePackageMutation, useGetPackageByIdQuery, useLazyFetchPartsQuery } from '../../../api/endpoints/packageDefinitionsApi';
import { useGetLaborsQuery } from '../../../api/endpoints/partLaborMapping';
import { toastService } from '../../../utils/toastService';
import { useLanguage } from '../../../contexts/LanguageContext';

// Parça arama sabitleri
const DEFAULT_PAGE_SIZE = 500;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_PAGE_SIZE = 1000;

function usePackageDefinitionsForm(onSaved) {
    const { th, currentLanguageId = 2, getCurrentLanguageId } = useLanguage();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [current, setCurrent] = useState(null);
    const [currentId, setCurrentId] = useState(null);
    const [isEditable, setIsEditable] = useState(true);

    const [packageName, setPackageName] = useState('');
    const [description, setDescription] = useState('');
    const [group, setGroup] = useState(null);
    const [status, setStatus] = useState(1);

    const [parts, setParts] = useState([]);
    const [labors, setLabors] = useState([]);

    const [selectedPartCode, setSelectedPartCode] = useState(null);
    const [quantity, setQuantity] = useState(1);

    const [selectedLaborCode, setSelectedLaborCode] = useState(null);
    const [laborTime, setLaborTime] = useState(1);

    // Parça arama için state ve ref
    const [partFilterValue, setPartFilterValue] = useState('');
    const filterTimeoutRef = useRef(null);

    const [createPackage, { isLoading: isCreating }] = useCreatePackageMutation();
    const [updatePackage, { isLoading: isUpdating }] = useUpdatePackageMutation();

    const [triggerFetchParts, { data: partsItems = [], isFetching: fetchingParts }] = useLazyFetchPartsQuery();

    const { data: laborsItems } = useGetLaborsQuery({ languageId: getCurrentLanguageId() || 2 }, { skip: !dialogVisible });

    const primePartsFetch = () => triggerFetchParts({ q: '', pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE, languageId: getCurrentLanguageId() || 2 });

    /**
     * Parça arama filter değişikliği
     * En az 3 karakter girildiğinde arama yapar, aksi halde default listeyi getirir
     */
    const handlePartFilterChange = useCallback(
        (value) => {
            // Filter değerini hemen güncelle
            setPartFilterValue(value);

            const trimmedValue = value.trim();

            // Önceki timer'ı temizle
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }

            filterTimeoutRef.current = setTimeout(() => {
                if (trimmedValue.length >= MIN_SEARCH_LENGTH) {
                    // 3+ karakter: arama yap
                    triggerFetchParts({
                        q: trimmedValue,
                        pageNumber: 1,
                        pageSize: SEARCH_PAGE_SIZE,
                        languageId: currentLanguageId || 2
                    });
                } else {
                    // 3'ten az karakter: default listeyi getir
                    triggerFetchParts({
                        q: '',
                        pageNumber: 1,
                        pageSize: DEFAULT_PAGE_SIZE,
                        languageId: currentLanguageId || 2
                    });
                }
            }, 300); // 300ms debounce
        },
        [triggerFetchParts, currentLanguageId]
    );

    useEffect(() => {
        const onEdit = (e) => {
            const row = e.detail;
            setEditMode(true);
            setCurrent(row);
            setCurrentId(row?.id || null);
            setIsEditable(row?.isEditable !== false);
            setDialogVisible(true);
            setSubmitted(false);
            setPackageName(row?.packageName || '');
            setDescription(row?.description || '');
            primePartsFetch();
        };
        document.addEventListener('pkg:edit', onEdit);
        return () => document.removeEventListener('pkg:edit', onEdit);
    }, [currentLanguageId]);

    const handleAddNew = () => {
        setEditMode(false);
        setCurrent(null);
        setCurrentId(null);
        setIsEditable(true);
        setSubmitted(false);
        setPackageName('');
        setDescription('');
        setGroup(null);
        setStatus(1);
        setParts([]);
        setLabors([]);
        setSelectedPartCode(null);
        setQuantity(1);
        setSelectedLaborCode(null);
        setLaborTime(1);
        setDialogVisible(true);
        primePartsFetch();
    };

    const { data: detailData } = useGetPackageByIdQuery({ id: currentId, languageId: currentLanguageId || 2 }, { skip: !editMode || !currentId });

    useEffect(() => {
        if (!editMode || !detailData) return;
        setGroup(detailData.group ?? null);
        setStatus(typeof detailData.status === 'number' ? detailData.status : 1);
        const mapped = Array.isArray(detailData.parts)
            ? detailData.parts.map((p) => ({
                  partCode: p.partCode,
                  partName: p.partName,
                  quantity: p.quantity ?? 1,
                  labors: p.labors ?? []
              }))
            : [];
        setParts(mapped);

        const mappedLabors = Array.isArray(detailData.labors)
            ? detailData.labors.map((l) => ({
                  laborCode: l.laborCode,
                  laborName: l.laborName,
                  laborTime: l.laborTime ?? 1
              }))
            : [];
        setLabors(mappedLabors);
    }, [detailData, editMode]);

    const hideDialog = () => {
        setDialogVisible(false);
        setPartFilterValue(''); // Dialog kapandığında filter'ı sıfırla
    };
    const removePart = (idx) => setParts((prev) => prev.filter((_, i) => i !== idx));

    const removeLabor = (idx) => setLabors((prev) => prev.filter((_, i) => i !== idx));

    const partOptions = useMemo(() => {
        const list = [];
        const src = Array.isArray(partsItems?.data) ? partsItems.data : Array.isArray(partsItems?.rows) ? partsItems.rows : Array.isArray(partsItems) ? partsItems : [];
        for (const i of src) {
            list.push({
                code: i.partCode,
                name: i.partName,
                label: `${i.partCode} – ${i.partName}`,
                labors: i.laborMappings ?? []
            });
        }
        return list;
    }, [partsItems]);

    const laborOptions = useMemo(() => {
        const list = [];
        const src = Array.isArray(laborsItems?.data) ? laborsItems.data : Array.isArray(laborsItems?.rows) ? laborsItems.rows : Array.isArray(laborsItems) ? laborsItems : [];
        for (const i of src) {
            list.push({
                code: i.code,
                name: i.text,
                label: i.text
            });
        }
        return list;
    }, [laborsItems]);

    const partDict = useMemo(() => {
        const d = {};
        for (const o of partOptions) d[o.code] = o;
        return d;
    }, [partOptions]);

    const laborDict = useMemo(() => {
        const d = {};
        for (const o of laborOptions) d[o.code] = o;
        return d;
    }, [laborOptions]);

    const addPart = () => {
        if (!selectedPartCode || !quantity) return;
        const sel = partDict[selectedPartCode];
        if (!sel) return;
        setParts((prev) => [
            ...prev,
            {
                partCode: sel.code,
                partName: sel.name,
                quantity: Number(quantity) || 1,
                labors: sel.labors
            }
        ]);
        setSelectedPartCode(null);
        setQuantity(1);
    };

    const addLabor = () => {
        if (!selectedLaborCode || !laborTime) return;
        const sel = laborDict[selectedLaborCode];
        if (!sel) return;
        setLabors((prev) => [
            ...prev,
            {
                laborCode: sel.code,
                laborName: sel.name,
                laborTime: Number(laborTime) || 1
            }
        ]);
        setSelectedLaborCode(null);
        setLaborTime(1);
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        if (!packageName?.trim() || !group) return false;

        const payload = {
            packageName: packageName.trim(),
            description: description || null,
            group: Number(group),
            status: Number(status),
            parts: parts.map((p) => ({
                partCode: p.partCode,
                partName: p.partName,
                quantity: p.quantity
            })),
            labors: labors.map((l) => ({
                laborCode: l.laborCode,
                laborName: l.laborName,
                laborTime: l.laborTime
            }))
        };

        try {
            if (editMode && current?.id) {
                await updatePackage({ ...payload, packageId: current.id }).unwrap();
                toastService.success(th('packageDefinitions.toast.updateSuccess'));
            } else {
                await createPackage(payload).unwrap();
                toastService.success(th('packageDefinitions.toast.createSuccess'));
            }
            setDialogVisible(false);
            onSaved?.();
            return true;
        } catch (e) {
            console.error(e);
            toastService.error(th('packageDefinitions.toast.saveError'));
            return false;
        }
    };

    const renderDialogFooter = (opts = {}) => {
        const onSave = opts.onSave ?? handleSubmit;
        return (
            <div className="flex justify-content-end gap-2">
                <button className="p-button p-button-outlined" type="button" onClick={hideDialog}>
                    <i className="pi pi-times mr-2" />
                    {th('common.cancel')}
                </button>
                {isEditable && (
                    <button className="p-button" onClick={onSave}>
                        <i className="pi pi-check mr-2" />
                        {th('common.save')}
                    </button>
                )}
            </div>
        );
    };

    return {
        dialogVisible,
        editMode,
        submitted,
        hideDialog,
        renderDialogFooter,
        packageName,
        description,
        group,
        status,
        parts,
        labors,
        setPackageName,
        setDescription,
        setGroup,
        setStatus,
        partOptions,
        selectedPartCode,
        setSelectedPartCode,
        fetchingParts,
        quantity,
        setQuantity,
        addPart,
        removePart,
        laborOptions,
        selectedLaborCode,
        setSelectedLaborCode,
        laborTime,
        setLaborTime,
        addLabor,
        removeLabor,
        handleAddNew,
        handleSubmit,
        isSaving: Boolean(isCreating || isUpdating),
        isEditable,
        // Parça arama için eklenen değerler
        partFilterValue,
        handlePartFilterChange
    };
}

export default usePackageDefinitionsForm;
